/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import PageContainer from "../page-container/page-container";
import { useNotification } from "../../context/notification-provider";
import LoadingScreen from "../progress/loading-screen";
import { ClientScoringRule, ClientWithRules } from "./types";
import useClientScoringRules from "./use-client-scoring-rules";
import { getClientScoringRules } from "./service";
import getColumns from "./data-grid";
import ExportToolbar, {
  ExportToolbarProps,
} from "../grid-toolbar/export-toolbar";
import EditClientScoringRuleDialog from "./edit-dialog";
import BulkEditClientScoringRuleDialog from "./bulk-edit-dialog";
import ScoringRuleFilterBar from "../scoring-rules/filters/scoring-rule-filter-bar";
import {
  EMPTY_SCORING_RULE_FILTERS,
  FilterableScoringRule,
  filterScoringRules,
  getRuleTypes,
} from "../scoring-rules/filters/scoring-rule-filters";

const toFilterable = ({
  scoringRule,
  disabled,
}: ClientScoringRule): FilterableScoringRule => ({
  ...scoringRule,
  clientDisabled: disabled,
});

const ClientScoringRules = () => {
  const { showNotification } = useNotification();

  const [selectedClient, setSelectedClient] = useState<ClientWithRules | null>(
    null,
  );
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<ClientScoringRule | null>(null);
  const [ruleToManageClients, setRuleToManageClients] =
    useState<ClientScoringRule | null>(null);
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [filters, setFilters] = useState(EMPTY_SCORING_RULE_FILTERS);

  const { data: clients, isLoading, error, mutate } = useClientScoringRules();

  // Memoised so the bulk dialog does not refetch on every render
  const activeClients = useMemo(
    () => clients?.filter((c) => !c.clientInformation.deleted) ?? [],
    [clients],
  );

  const allRules = useMemo(
    () => selectedClient?.scoringRules ?? [],
    [selectedClient],
  );
  const ruleTypes = useMemo(
    () => getRuleTypes(allRules.map(toFilterable)),
    [allRules],
  );
  const filteredRules = useMemo(
    () => filterScoringRules(allRules, filters, toFilterable),
    [allRules, filters],
  );

  useEffect(() => {
    const loadInitialClient = async () => {
      if (clients && clients.length > 0 && !selectedClient) {
        setIsLoadingRules(true);
        try {
          const rules = await getClientScoringRules(
            clients[0].clientInformation.id,
          );
          setSelectedClient({
            ...clients[0],
            scoringRules: rules,
          });
        } catch (err) {
          showNotification({
            message: `Failed to load scoring rules: ${(err as Error).message}`,
            severity: "error",
            duration: 3000,
          });
        } finally {
          setIsLoadingRules(false);
        }
      }
    };

    loadInitialClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  const mutateSelectedClient = React.useCallback(async () => {
    if (!selectedClient) return;

    try {
      const rules = await getClientScoringRules(
        selectedClient.clientInformation.id,
      );
      setSelectedClient({
        ...selectedClient,
        scoringRules: rules,
      });
      await mutate();
    } catch (err) {
      showNotification({
        message: `Failed to refresh scoring rules: ${(err as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    }
  }, [selectedClient, mutate, showNotification]);

  const handleOnRefreshClick = React.useCallback(() => {
    showNotification({
      message: `Refreshing client scoring rules data...`,
      severity: "info",
      duration: 2000,
    });
    mutateSelectedClient();
  }, [showNotification, mutateSelectedClient]);

  const handleOnEdit = React.useCallback((rule: ClientScoringRule) => {
    setRuleToEdit(rule);
    setIsEditDialogOpen(true);
  }, []);

  const handleOnManageClients = React.useCallback(
    (rule: ClientScoringRule) => setRuleToManageClients(rule),
    [],
  );

  const handleManageClientsDialogClose = (saved: boolean) => {
    setRuleToManageClients(null);
    if (saved) mutateSelectedClient();
  };

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
    setRuleToEdit(null);
    mutateSelectedClient();
  };

  const handleClientChange = async (event: SelectChangeEvent<number>) => {
    const clientId = event.target.value as number;
    const client = clients?.find((c) => c.clientInformation.id === clientId);
    if (client) {
      setIsLoadingRules(true);
      try {
        const rules = await getClientScoringRules(client.clientInformation.id);
        setSelectedClient({
          ...client,
          scoringRules: rules,
        });
      } catch (err) {
        showNotification({
          message: `Failed to load scoring rules: ${(err as Error).message}`,
          severity: "error",
          duration: 3000,
        });
      } finally {
        setIsLoadingRules(false);
      }
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading clients data.
      </Alert>
    );
  }

  const exportFileName = `client-scoring-rules-${(
    selectedClient?.clientInformation.name ?? "client"
  )
    .replace(/[^a-z0-9]+/gi, "-")
    .toLowerCase()}`;

  return (
    <PageContainer
      title="Client Scoring Rules"
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="right" enterDelay={1000}>
            <div>
              <IconButton
                size="small"
                aria-label="refresh"
                onClick={handleOnRefreshClick}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
        </Stack>
      }
    >
      <Box
        sx={{
          flex: 1,
          width: "100%",
          height: "100%",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="client-select-label">Select Client</InputLabel>
            <Select
              labelId="client-select-label"
              id="client-select"
              value={selectedClient?.clientInformation.id || ""}
              label="Select Client"
              onChange={handleClientChange}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 500,
                  },
                },
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
              }}
            >
              {activeClients.map((client) => (
                <MenuItem
                  key={client.clientInformation.id}
                  value={client.clientInformation.id}
                >
                  {client.clientInformation.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <ScoringRuleFilterBar
          filters={filters}
          onChange={setFilters}
          ruleTypes={ruleTypes}
          showClientStatus
          shownCount={filteredRules.length}
          totalCount={allRules.length}
        />

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading clients data.
          </Alert>
        ) : (
          <Paper
            elevation={2}
            sx={{
              height: "600px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <DataGrid
              slots={{ toolbar: ExportToolbar }}
              slotProps={{
                toolbar: { fileName: exportFileName } as ExportToolbarProps,
              }}
              showToolbar
              rows={filteredRules}
              getRowId={(row: ClientScoringRule) => row.scoringRule.id}
              columns={getColumns({
                onEdit: handleOnEdit,
                onManageClients: handleOnManageClients,
              })}
              disableRowSelectionOnClick
              loading={isLoadingRules}
              sx={{
                height: "100%",
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                  outline: "transparent",
                },
                [`& .${gridClasses.columnHeader}`]: {
                  backgroundColor: "#f5f6fa",
                  outline: "transparent",
                },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: "none",
                  },
                [`& .${gridClasses.row}:hover`]: {
                  cursor: "pointer",
                },
              }}
            />
          </Paper>
        )}
      </Box>
      {isEditDialogOpen && (
        <EditClientScoringRuleDialog
          open={isEditDialogOpen}
          clientScoringRule={ruleToEdit}
          clientId={selectedClient?.clientInformation.id || null}
          clientName={selectedClient?.clientInformation.name || ""}
          onClose={handleEditDialogClose}
        />
      )}
      {ruleToManageClients && (
        <BulkEditClientScoringRuleDialog
          open
          clientScoringRule={ruleToManageClients}
          clients={activeClients}
          onClose={handleManageClientsDialogClose}
        />
      )}
    </PageContainer>
  );
};

export default ClientScoringRules;
